import { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const NOMBRE = 'CORPORACION E INVERSIONES SULLANA LA PERLA DEL CHIRA SAC';
const RUC = '20613199579';
const BCP = '5357079899068';
const TITLE = 'MOVIMIENTOS DE CTA';

const buildSheetData = (rows) => {
  const header = [
    [NOMBRE],
    [`RUC: ${RUC}`],
    [''],
    [TITLE],
    [`BCP : ${BCP}`],
    [''],
    ['DIA', 'DESCRIPCION', 'CUOTA', 'MONTO', 'SALDO'],
  ];

  const body = rows.map((row) => {
    const monto = Number.isFinite(Number(row?.monto)) ? Number(row.monto) : '';
    const saldo = Number.isFinite(Number(row?.saldo)) ? Number(row.saldo) : '';
    const tipo = String(row?.tipo || '').toLowerCase();
    const descripcion = tipo === 'prestamo'
      ? `DESEMBOLSO ${row?.descripcion || '-'}`.trim()
      : (row?.descripcion || '-');
    return [
      row?.dia || '-',
      descripcion,
      row?.cuota ?? '-',
      monto,
      saldo,
    ];
  });

  return header.concat(body);
};

const formatMoneyColumns = (ws, startRow, endRow) => {
  for (let row = startRow; row <= endRow; row++) {
    ['D', 'E'].forEach((col) => {
      const cellRef = `${col}${row}`;
      if (ws[cellRef] && typeof ws[cellRef].v === 'number') {
        ws[cellRef].z = '#,##0.00';
        ws[cellRef].t = 'n';
      }
    });
  }
};

const ReporteFinanzasExcel = ({ rows = [], filters = {}, onComplete, onExcelGenerated }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const hasGeneratedRef = useRef(false);

  const generateExcelReport = useCallback(() => {
    if (isGenerating) return;
    setIsGenerating(true);

    try {
      if (!rows.length) {
        toast.warning('No hay movimientos para generar el Excel.');
        return;
      }

      const wb = XLSX.utils.book_new();
      const abonadoPor = filters?.abonado_por || '';

      const buildAndAppend = (sheetRows, sheetName) => {
        const aoa = buildSheetData(sheetRows);
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [
          { wch: 12 },
          { wch: 50 },
          { wch: 10 },
          { wch: 14 },
          { wch: 14 },
        ];

        const dataStartRow = 8;
        const dataEndRow = dataStartRow + sheetRows.length - 1;
        if (sheetRows.length > 0) {
          formatMoneyColumns(ws, dataStartRow, dataEndRow);
        }

        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      };

      if (!abonadoPor) {
        const cuentaCorriente = rows.filter((r) => r?.abonado_por === 'CUENTA CORRIENTE');
        const cajaChica = rows.filter((r) => r?.abonado_por === 'CAJA CHICA');
        buildAndAppend(cuentaCorriente, 'CUENTA CORRIENTE');
        buildAndAppend(cajaChica, 'CAJA CHICA');
      } else {
        buildAndAppend(rows, abonadoPor);
      }

      const excelBuffer = XLSX.write(wb, {
        bookType: 'xlsx',
        type: 'array',
        cellStyles: true,
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);

      if (onExcelGenerated) {
        onExcelGenerated(url);
      }

      toast.success(`Excel generado correctamente. ${rows.length} movimientos procesados.`);
    } catch (error) {
      console.error('Error generando Excel:', error);
      toast.error('Error al generar el Excel: ' + error.message);
    } finally {
      setIsGenerating(false);
      if (onComplete) onComplete();
    }
  }, [filters?.abonado_por, isGenerating, onComplete, onExcelGenerated, rows]);

  useEffect(() => {
    if (rows.length > 0 && !isGenerating && !hasGeneratedRef.current) {
      hasGeneratedRef.current = true;
      generateExcelReport();
    }
  }, [generateExcelReport, isGenerating, rows.length]);

  return (
    <div className="hidden">
      {isGenerating && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white p-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Generando Excel...
          </div>
        </div>
      )}
    </div>
  );
};

export default ReporteFinanzasExcel;
